import { useEffect, useState, useRef, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ChatDialog({ kindId, open, onClose, onSent }) {
  const { user } = useAuth();
  const [thread, setThread] = useState({ kind: null, messages: [] });
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    if (!kindId) return;
    setLoading(true);
    try {
      const r = await api.get(`/chat/${kindId}`);
      setThread(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [kindId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [open, load]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [thread.messages.length]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      await api.post("/mentorbericht", { kind_id: kindId, body: text });
      setBody("");
      await load();
      if (onSent) onSent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSending(false);
  };

  const kind = thread.kind;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg p-0 overflow-hidden" data-testid="chat-dialog">
        <DialogHeader className="p-4 border-b border-neutral-200 bg-[color:var(--sage-100)]/40">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[color:var(--sage)] text-white font-semibold">
              {kind ? `${kind.voornaam[0]}${kind.achternaam[0]}` : <MessageSquare className="h-4 w-4" />}
            </div>
            <div>
              <div className="text-base">Chat over {kind ? `${kind.voornaam} ${kind.achternaam}` : "kind"}</div>
              <div className="text-xs text-neutral-500 font-normal">Live gesprek met ouders</div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="h-96 overflow-y-auto p-4 space-y-2 bg-[color:var(--bone)]" data-testid="chat-messages">
          {loading && thread.messages.length === 0 && (
            <div className="text-center text-sm text-neutral-500 py-8">Chat laden…</div>
          )}
          {!loading && thread.messages.length === 0 && (
            <div className="text-center text-sm text-neutral-500 py-8">
              Nog geen berichten. Start het gesprek hieronder.
            </div>
          )}
          {thread.messages.map(m => {
            const mine = m.van_user_id === user?.id;
            const fromOuder = m.van_type === "ouder";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.id}`}
                  className={`max-w-[75%] rounded-lg px-3 py-2 ${
                    mine
                      ? "bg-[color:var(--sage)] text-white"
                      : fromOuder
                        ? "bg-[color:var(--terracotta)]/10 border border-[color:var(--terracotta)]/30 text-neutral-800"
                        : "bg-white border border-neutral-200 text-neutral-800"
                  }`}
                >
                  {!mine && (
                    <div className={`text-[10px] uppercase tracking-widest font-semibold mb-1 ${fromOuder ? "text-[color:var(--terracotta)]" : "text-neutral-500"}`}>
                      {m.van_naam} · {fromOuder ? "Ouder" : m.van_rol}
                    </div>
                  )}
                  {m.onderwerp && <div className="font-semibold text-sm mb-1">{m.onderwerp}</div>}
                  <div className="text-sm whitespace-pre-line">{m.body}</div>
                  <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-neutral-400"}`}>
                    {new Date(m.created_at).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        <div className="p-3 border-t border-neutral-200 bg-white">
          <div className="flex items-end gap-2">
            <Textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
              }}
              placeholder="Schrijf een bericht… (Cmd/Ctrl+Enter om te verzenden)"
              rows={2}
              className="resize-none"
              data-testid="chat-input"
            />
            <Button
              onClick={send}
              disabled={sending || !body.trim()}
              data-testid="chat-send"
              style={{ background: "var(--sage)" }}
              className="text-white h-10"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
